import type { GetDeviceActionLogQueryParamsSortEnumKey } from '@emma/api_client/types';
import { Box, Flex, InlineNotification } from '@lego/klik-ui';
import type { InferRequestType } from 'hono/client';
import { useDebounceValue } from 'usehooks-ts';
import { deviceActionLogTableColumns } from '@/components/common/table/column_definitions/DeviceActionLog';
import { TableStoreProvider, useTableStore } from '@/components/common/table/store/Context';
import { Table } from '@/components/common/table/Table';
import type { ApiClient } from '@/hooks/api';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { useDeviceActionLog } from '@/hooks/device/device_action_log_resource';

const MIN_CORE_VERSION = 15;
const FILTER_DEBOUNCE_MS = 300;

type GetDeviceActionLog = ApiClient['v1']['devices'][':deviceId']['actions']['log']['$get'];
type DeviceActionLogQuery = InferRequestType<GetDeviceActionLog>['query'];

const FILTERS: FilterConfig = {
  action: 'actionLike',
  correlationId: 'correlationId',
  createdAt: ['createdAfter', 'createdBefore'],
  deviceTimestamp: ['deviceTimestampAfter', 'deviceTimestampBefore'],
  errorMessage: 'errorMessageLike',
  status: 'statusIn',
  updatedAt: ['updatedAfter', 'updatedBefore'],
};

type Props = {
  deviceId: number;
  reportedCoreVersion: string | null;
};

const DeviceActionLogContent = ({ deviceId }: { deviceId: number }) => {
  const { filters, pagination, sorting } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);

  const queryParams: DeviceActionLogQuery = {
    ...serverTableParams<GetDeviceActionLogQueryParamsSortEnumKey>(pagination, sorting),
    ...buildFilterParams(debouncedFilters, FILTERS),
  };

  const { data, isLoading, isValidating, mutate } = useDeviceActionLog(deviceId, queryParams);

  const totalItems = data?.itemCount ?? 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  return (
    <Table
      columns={deviceActionLogTableColumns}
      data={data?.items ?? []}
      description="A log of actions performed on this device, including their status and correlation IDs for tracing"
      isLoading={Boolean(isLoading || !data)}
      isValidating={isValidating}
      refresh={mutate}
      serverSide={{ pageCount, rowCount: totalItems }}
      title="Action Log"
    />
  );
};

export const DeviceActionLog = ({ deviceId, reportedCoreVersion }: Props) => {
  const coreVersion = Number.parseInt(reportedCoreVersion?.replace(/^v/, '') ?? '', 10);
  if (Number.isNaN(coreVersion) || coreVersion < MIN_CORE_VERSION) {
    const versionLabel = reportedCoreVersion
      ? `core ${reportedCoreVersion}`
      : 'an unknown core version';

    return (
      <Box marginTop={4}>
        <InlineNotification variant="info">
          <InlineNotification.Content>
            <InlineNotification.Title>
              Core version {MIN_CORE_VERSION} required
            </InlineNotification.Title>
            <InlineNotification.Description>
              Action Log requires core version {MIN_CORE_VERSION} or above. This device is running{' '}
              {versionLabel}.
            </InlineNotification.Description>
          </InlineNotification.Content>
        </InlineNotification>
      </Box>
    );
  }

  return (
    <Flex direction="column" gap={8}>
      <TableStoreProvider initialSorting={[{ desc: true, id: 'createdAt' }]} name="DeviceActionLog">
        <DeviceActionLogContent deviceId={deviceId} />
      </TableStoreProvider>
    </Flex>
  );
};
